import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import type { HouseholdDto, HouseholdRole, MeResponse, UserDto } from '@fewerbags/schema';
import { api, queryKeys, UNAUTHORIZED_EVENT } from './api';

export type AuthState =
  | { status: 'loading'; user: null; household: null; role: null; memberships: [] }
  | { status: 'anon'; user: null; household: null; role: null; memberships: [] }
  | {
      status: 'authed';
      user: UserDto;
      household: HouseholdDto | null;
      role: HouseholdRole | null;
      memberships: MeResponse['memberships'];
    };

interface AuthContextValue {
  auth: AuthState;
  /** Re-fetch /auth/me (after login, register, household changes). */
  refresh: () => Promise<MeResponse | null>;
  /** Replace the cached `me` immediately (e.g. from a login response). */
  setMe: (me: MeResponse | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const EMPTY = { user: null, household: null, role: null, memberships: [] as [] };

/** Paths where an unexpected 401 should NOT bounce to /login (they are the login flow). */
const PUBLIC_PREFIXES = [
  '/login',
  '/register',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
];

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.auth.me(),
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    const onUnauthorized = () => {
      qc.setQueryData(queryKeys.me, null);
      qc.removeQueries({
        predicate: (q) => q.queryKey[0] !== 'server' && q.queryKey[0] !== 'auth',
      });
      const here = location.pathname + location.search;
      if (!PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p))) {
        void navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
      }
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [qc, navigate, location.pathname, location.search]);

  const logoutMutation = useMutation({
    mutationFn: () => api.auth.logout(),
    onSettled: () => {
      qc.setQueryData(queryKeys.me, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'server' });
      void navigate('/login', { replace: true });
    },
  });

  const value = useMemo<AuthContextValue>(() => {
    let auth: AuthState;
    if (me.isPending) auth = { status: 'loading', ...EMPTY };
    else if (!me.data) auth = { status: 'anon', ...EMPTY };
    else
      auth = {
        status: 'authed',
        user: me.data.user,
        household: me.data.household,
        role: me.data.role,
        memberships: me.data.memberships,
      };
    return {
      auth,
      refresh: async () => {
        const r = await qc.fetchQuery({
          queryKey: queryKeys.me,
          queryFn: () => api.auth.me(),
          staleTime: 0,
        });
        return r;
      },
      setMe: (data) => qc.setQueryData(queryKeys.me, data),
      logout: async () => {
        await logoutMutation.mutateAsync().catch(() => undefined);
      },
    };
  }, [me.isPending, me.data, qc, logoutMutation]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Where to go after a successful sign-in: the `next` param if it is a safe in-app path. */
export function safeNext(search: string, fallback = '/'): string {
  const next = new URLSearchParams(search).get('next');
  if (next && next.startsWith('/') && !next.startsWith('//')) return next;
  return fallback;
}
